import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '@tally/shared';
import { Repeat, X } from 'lucide-react';
import { insightsApi } from '@/services/api';
import { ROUTES } from '@/routes';
import { formatUsd } from '@/utils/money';
import { durationLabel, monthLabel, monthsBetween } from '@/utils/time';
import { AXIS_TICK, CHART_TOOLTIP, compactUsd } from '@/components/charts/chartTheme';

/** Everything about one merchant: how much, how long, per period and per year. */
export function MerchantPanel({ merchantKey, name, onClose }: { merchantKey: string; name: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['insights-merchant', merchantKey], queryFn: () => insightsApi.merchant(merchantKey) });
  const d = data?.data;

  // Portaled: inside a space-y container the sibling margin would push the fixed overlay down.
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative h-full w-full max-w-lg overflow-y-auto border-l border-border bg-card shadow-xl animate-in slide-in-from-right" role="dialog" aria-label={name}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-5 py-4">
          <h2 className="truncate text-lg font-semibold">{d?.name ?? name}</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-accent" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading && <div className="space-y-3 p-5">{[...Array(4)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-muted" />)}</div>}
        {error && <p className="p-5 text-sm text-destructive">{error instanceof Error ? error.message : 'Could not load this merchant'}</p>}

        {d && (
          <div className="space-y-6 p-5">
            <div>
              <p className="text-3xl font-bold">{formatUsd(d.totalUsd)}</p>
              <p className="text-sm text-muted-foreground">
                {d.count} charge{d.count === 1 ? '' : 's'} · avg {formatUsd(d.avgUsd)} · {monthLabel(d.first)} – {monthLabel(d.last)}
                {' '}({durationLabel(monthsBetween(d.first, d.last))})
              </p>
            </div>

            {d.subscriptions.map((s) => (
              <div key={s.key} className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Repeat className="h-3.5 w-3.5" /> {s.cadence} subscription · {s.active ? 'active' : 'ended'}
                </p>
                <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-xs text-muted-foreground">Price</p><p className="font-medium">{formatCurrency(s.typicalAmount, s.currency)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Per month</p><p className="font-medium">{s.monthlyUsd === null ? '—' : formatUsd(s.monthlyUsd)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Per year</p><p className="font-medium">{s.yearlyUsd === null ? '—' : formatUsd(s.yearlyUsd)}</p></div>
                </div>
              </div>
            ))}

            <div>
              <p className="mb-2 text-xs font-medium text-muted-foreground">Spent over</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {d.periods.map((p) => (
                  <div key={p.key} className="rounded-lg border border-border p-3">
                    <p className="text-xs text-muted-foreground">{p.label}</p>
                    <p className="font-semibold">{formatUsd(p.totalUsd)}</p>
                    <p className="text-xs text-muted-foreground">{p.count} charge{p.count === 1 ? '' : 's'}</p>
                  </div>
                ))}
              </div>
            </div>

            {d.byYear.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-medium text-muted-foreground">Per year</p>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={d.byYear}>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="year" tick={AXIS_TICK} />
                      <YAxis tick={AXIS_TICK} tickFormatter={compactUsd} width={48} />
                      <Tooltip {...CHART_TOOLTIP} formatter={(v: number) => [formatUsd(v), 'Spent']} />
                      <Bar dataKey="totalUsd" fill="#6366f1" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <Link
              to={`${ROUTES.app.transactions}?${new URLSearchParams({ searchQuery: d.name }).toString()}`}
              className="block rounded-lg border border-border px-4 py-2 text-center text-sm font-medium hover:bg-accent"
            >
              View all charges
            </Link>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
